/**
 * Display names for ArkBridge tape statuses, resolved from `status_id`.
 *
 * ── Why this file exists ─────────────────────────────────────────────────────
 * `vwItemsAPI` derives its `Status` string with a hardcoded CASE that only covers IDs 1–4:
 *
 *   CASE i.Status WHEN 1 THEN 'Pending' WHEN 2 THEN 'Catalogued' WHEN 3 THEN 'Read'
 *                 WHEN 4 THEN 'Uploaded' ELSE 'Unknown' END AS Status
 *
 * So the API's `status` field reads 'Unknown' for every status from 5 upward — nine of the
 * fourteen statuses can never be displayed. The same view also exposes the raw integer as
 * `StatusID`, surfaced by the API as `status_id`, which is correct for every status. Mapping
 * that integer here is what lets the portal show a real name without a shared-view change.
 *
 * The names below are `noah.status.name` — the status table in ArkBridge — and are also the
 * published set: backup_saas/Docs.md 'Status Values' documents all fourteen with these exact
 * names. They deliberately differ from the mnemonics in `arkbridge_constants.py`
 * (`in_progress`, `extract_ok`): those are developer identifiers and read as internal noise in
 * a customer-facing table. See the three-way name table in
 * views/Admin/Job/JobManagement/Processing/processingConfig.js.
 *
 * Counting is a separate concern and is NOT affected by any of this — count filters are sent
 * as `status_ids` and never touch the string. Use the `TAPE_*_STATUS_IDS` groups in
 * processingConfig.js for that; this file is display only.
 *
 * Tracked as STATUS-DISPLAY-FIX in ACTION_ITEM_status_display_fix.md. If the view is ever
 * fixed to join `noah.status` directly, this map becomes redundant and `tapeStatusLabel` can
 * fall back to the API string permanently — the fallback already does exactly that.
 */

/** ID → the name held in ArkBridge's `noah.status` table. 0 is the sentinel for an unexpected state. */
export const TAPE_STATUS_DISPLAY_NAMES = {
    0: 'Unknown',
    1: 'Created',
    2: 'Cataloged',
    3: 'Read Success',
    4: 'Upload Success',
    5: 'Loaded in Drive',
    6: 'Read in Progress',
    7: 'Read Fail',
    8: 'Unloaded from Drive',
    9: 'Extract in Progress',
    10: 'Extract Failed',
    11: 'Extract Success',
    12: 'Upload in Progress',
    13: 'Upload Failed',
};

/**
 * Resolves a tape row's status to a display name.
 *
 * Prefers `status_id` because it is correct for every status; falls back to the API's `status`
 * string when the id is absent or unrecognised. The fallback matters: a payload that carries
 * `status` but not `status_id` still renders, and an id we have never seen shows whatever the
 * back end called it rather than a blank cell.
 *
 * @param {object} row - A tape row from `man_item_list` / `man_item_get`.
 * @returns {string} - The status name, or '' when the row carries no status at all.
 */
export const tapeStatusLabel = (row) => {
    const id = parseInt(row?.status_id, 10);
    if (!isNaN(id) && TAPE_STATUS_DISPLAY_NAMES[id] !== undefined) return TAPE_STATUS_DISPLAY_NAMES[id];
    return row?.status ?? '';
};
